import React from "react";
import { Sparkles } from "lucide-react";

/**
 * Phase 18.16-A — PageBanner upgraded to match the new homepage hero.
 *
 * Used by: Paklijst, Boekingsformulier, Factuur maken, CBM calculator,
 * Volumemeter, Webshop. Upgrading this single file harmonises every
 * tool-style page in one shot without touching their business logic.
 *
 * Visual match with `PageHero` from `/components/shared/PageShell.jsx`:
 *   - Background: production gradient (#082c50 → #0a335c → #194d8d)
 *     when no `image` is provided, OR full-bleed photo + dark gradient
 *     overlay when an image is set.
 *   - Soft cyan / blue blur blobs for the premium logistics-tech feel.
 *   - Pill-shaped eyebrow with Sparkles icon by default.
 *   - 4xl→6xl scaling h1 with optional gradient highlight (the old
 *     "subtitle" prop is rendered as a gradient highlight when
 *     `highlight` is unset — backwards compatible).
 *
 * Props are 100% backwards compatible — old call-sites work unchanged.
 *
 * Props:
 *   image       absolute https URL (optional now — falls back to gradient)
 *   imageAlt    alt-text
 *   eyebrow     small chip above the H1 (optional)
 *   eyebrowIcon lucide icon component for the chip (defaults to Sparkles)
 *   title       H1 (required)
 *   subtitle    legacy: rendered as block + cyan accent (kept as fallback)
 *   highlight   optional: gradient highlight appended to title inline
 *   description paragraph below the title (optional)
 *   compact     smaller variant (h1 3xl→5xl, less padding)
 *   testid      data-testid on the <section> wrapper
 *   children    extra controls rendered below the description (e.g. CTA row)
 */
export default function PageBanner({
  image,
  imageAlt,
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  subtitle,
  highlight,
  description,
  compact = false,
  testid = "page-banner",
  children,
}) {
  const IconForChip = EyebrowIcon || Sparkles;
  return (
    <section
      className="relative overflow-hidden text-white bg-[#082c50]"
      data-testid={testid}
    >
      <div className="absolute inset-0">
        {image ? (
          <>
            <img
              src={image}
              alt={imageAlt || title || ""}
              className="absolute inset-0 w-full h-full object-cover object-center"
              loading="eager"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#082c50]/85 via-[#082c50]/65 to-[#1a5193]/55" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#082c50]/55 via-transparent to-transparent" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-[#082c50] via-[#0a335c] to-[#194d8d]" />
            <div className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-cyan-400/10 blur-[120px]" />
            <div className="absolute top-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#1a5193]/40 blur-[140px]" />
          </>
        )}
      </div>

      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${compact ? "py-12 sm:py-16 lg:py-20" : "py-16 sm:py-20 lg:py-24"}`}>
        <div className="max-w-3xl">
          {eyebrow && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[11px] tracking-[0.18em] uppercase font-chivo font-bold">
              <IconForChip className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2} />
              {eyebrow}
            </span>
          )}
          <h1 className={`font-chivo font-black tracking-tight leading-[1.04] mt-5 ${compact ? "text-3xl sm:text-4xl lg:text-5xl" : "text-4xl sm:text-5xl lg:text-6xl"}`}>
            {title}
            {highlight && (
              <>
                {" "}
                <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-white bg-clip-text text-transparent">
                  {highlight}
                </span>
              </>
            )}
            {!highlight && subtitle && (
              <span className="block mt-2 bg-gradient-to-r from-cyan-300 via-sky-300 to-white bg-clip-text text-transparent">
                {subtitle}
              </span>
            )}
          </h1>
          {description && (
            <p className="mt-5 max-w-2xl text-white/85 text-lg leading-relaxed">
              {description}
            </p>
          )}
          {children && <div className="mt-7">{children}</div>}
        </div>
      </div>
    </section>
  );
}
